import { Box, Center, HStack, Image, SimpleGrid, Stack, VStack } from '@chakra-ui/react';
import { useGetProfileRoastsQuery, type ProfileRoast } from 'hooks/queries/profile/useGetProfileRoastsQuery';

import { ReactComponent as NoProjectsCompletedIllustration } from 'assets/illustrations/NoProjectsCompleted.svg';
import Loader from 'components/Loader';
import Text from 'components/Text';
import { getOverallScoreColor, getRoastDisplayName, getStatusBadge, ROAST_PUBLIC_BASE } from 'features/roast/utils';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

interface RoastCardProps {
  roast: ProfileRoast;
}

const RoastCard = ({ roast }: RoastCardProps) => {
  const overallScore = roast.scores?.overall ?? null;
  const statusBadge = getStatusBadge(roast.status);
  const isLive = roast.status === 'ready';

  const cardContent = (
    <Box
      backgroundColor="whiteAlpha.100"
      borderRadius="12px"
      overflow="hidden"
      transition="all 0.2s"
      _hover={isLive ? { backgroundColor: 'whiteAlpha.200', transform: 'translateY(-2px)' } : {}}
      cursor={isLive ? 'pointer' : 'default'}
    >
      {/* Screenshot */}
      <Box height="160px" backgroundColor="whiteAlpha.50" overflow="hidden">
        {roast.screenshot_url ? (
          <Image
            src={roast.screenshot_url}
            alt={getRoastDisplayName(roast)}
            width="100%"
            height="100%"
            objectFit="cover"
          />
        ) : (
          <Center height="100%">
            <Text fontSize="xLarge" opacity="tertiary">
              {roast.status === 'processing' ? '...' : '?'}
            </Text>
          </Center>
        )}
      </Box>

      {/* Content */}
      <VStack spacing={2} p={3} alignItems="flex-start">
        <HStack width="100%" justifyContent="space-between">
          <Text fontSize="small" fontWeight="semibold" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxWidth="60%">
            {getRoastDisplayName(roast)}
          </Text>
          <Box px={2} py={0.5} borderRadius="6px" backgroundColor={statusBadge.bg}>
            <Text fontSize="xMicro" color={statusBadge.color} fontWeight="semibold">
              {statusBadge.label}
            </Text>
          </Box>
        </HStack>

        {overallScore !== null && (
          <HStack spacing={2}>
            <Text fontSize="large" fontWeight="bold" color={getOverallScoreColor(overallScore)}>
              {overallScore}/100
            </Text>
            <HStack spacing={3}>
              <HStack spacing={1}>
                <Text fontSize="xMicro" opacity="tertiary">
                  ▲ {roast.upvote_count ?? 0}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="xMicro" opacity="tertiary">
                  💬 {roast.comment_count ?? 0}
                </Text>
              </HStack>
            </HStack>
          </HStack>
        )}

        {roast.status === 'processing' && (
          <Text fontSize="xMicro" opacity="tertiary">
            Launching on {roast.reveal_date}
          </Text>
        )}
      </VStack>
    </Box>
  );

  if (isLive) {
    return (
      <a
        href={`${ROAST_PUBLIC_BASE}/${roast.slug}/`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        {cardContent}
      </a>
    );
  }

  return cardContent;
};

interface ProfileRoastsProps {
  userId?: string | null;
}

const ProfileRoasts = ({ userId }: ProfileRoastsProps) => {
  const { data: roasts, isPending } = useGetProfileRoastsQuery({ userId });
  const { isSmallScreen } = useMediaQueries();

  if (isPending) {
    return (
      <Center width="100%" height="378px">
        <Loader />
      </Center>
    );
  }

  if (!roasts || roasts.length === 0) {
    return (
      <Center width="100%">
        <Stack spacing={5}>
          {/* @ts-ignore */}
          <NoProjectsCompletedIllustration width={isSmallScreen ? '90%' : '100%'} />
          <Text fontSize="xLarge" fontWeight="regular" textAlign="center">
            No roasts yet.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <SimpleGrid columns={isSmallScreen ? 1 : [1, 1, 2, 2, 3]} gap={4} width="100%">
      {roasts.map((roast) => (
        <RoastCard key={roast.id} roast={roast} />
      ))}
    </SimpleGrid>
  );
};

export default ProfileRoasts;
